import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { chainApi, type ChainAdminRow, type ChainHospital } from "@/api/chain";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

type AdminForm = {
  name: string;
  email: string;
  password: string;
  adminScope: "CHAIN" | "HOSPITAL";
  hospitalId: string;
  status: "ACTIVE" | "INACTIVE";
};

const emptyForm = (): AdminForm => ({
  name: "",
  email: "",
  password: "",
  adminScope: "HOSPITAL",
  hospitalId: "",
  status: "ACTIVE",
});

function AdminFormFields({
  form,
  setForm,
  hospitals,
  passwordRequired,
  showStatus,
}: {
  form: AdminForm;
  setForm: (updater: AdminForm | ((prev: AdminForm) => AdminForm)) => void;
  hospitals: ChainHospital[];
  passwordRequired?: boolean;
  showStatus?: boolean;
}) {
  return (
    <div className="space-y-3">
      <div>
        <Label>Type</Label>
        <Select
          value={form.adminScope}
          onValueChange={(v) =>
            setForm((f) => ({ ...f, adminScope: v as "CHAIN" | "HOSPITAL" }))
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="CHAIN">Chain admin</SelectItem>
            <SelectItem value="HOSPITAL">Sub-admin (one hospital)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {form.adminScope === "HOSPITAL" && (
        <div>
          <Label>Hospital</Label>
          <Select
            value={form.hospitalId}
            onValueChange={(v) => setForm((f) => ({ ...f, hospitalId: v }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select hospital" />
            </SelectTrigger>
            <SelectContent>
              {hospitals.map((h) => (
                <SelectItem key={h.id} value={h.id}>
                  {h.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div>
        <Label>Name</Label>
        <Input
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
      </div>
      <div>
        <Label>Email</Label>
        <Input
          type="email"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
        />
      </div>
      <div>
        <Label>{passwordRequired ? "Password" : "New password"}</Label>
        <Input
          type="password"
          value={form.password}
          placeholder={passwordRequired ? undefined : "Leave blank to keep current password"}
          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
        />
      </div>
      {showStatus ? (
        <div>
          <Label>Status</Label>
          <Select
            value={form.status}
            onValueChange={(v) =>
              setForm((f) => ({ ...f, status: v as "ACTIVE" | "INACTIVE" }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="INACTIVE">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : null}
    </div>
  );
}

export default function ChainAdminsPage() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<ChainAdminRow | null>(null);
  const [createForm, setCreateForm] = useState<AdminForm>(emptyForm);
  const [editForm, setEditForm] = useState<AdminForm>(emptyForm);

  const { data: group } = useQuery({
    queryKey: ["chain-group"],
    queryFn: async () => {
      const r = await chainApi.getGroup();
      return r.data.data as {
        maxUsers: number;
        usage: { userCount: number };
        hospitals: ChainHospital[];
      };
    },
  });

  const { data: admins = [], isLoading } = useQuery({
    queryKey: ["chain-admins"],
    queryFn: async () => {
      const r = await chainApi.listAdmins();
      return (r.data.data ?? []) as ChainAdminRow[];
    },
  });

  const createMut = useMutation({
    mutationFn: () =>
      chainApi.createAdmin({
        name: createForm.name,
        email: createForm.email,
        password: createForm.password,
        adminScope: createForm.adminScope,
        hospitalId: createForm.adminScope === "HOSPITAL" ? createForm.hospitalId : "",
      }),
    onSuccess: () => {
      toast.success("Admin created");
      setCreateOpen(false);
      setCreateForm(emptyForm());
      qc.invalidateQueries({ queryKey: ["chain-admins"] });
      qc.invalidateQueries({ queryKey: ["chain-group"] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      toast.error(e.response?.data?.message ?? "Failed to create admin");
    },
  });

  const updateMut = useMutation({
    mutationFn: () => {
      if (!editingAdmin) throw new Error("No admin selected");
      const body: Parameters<typeof chainApi.updateAdmin>[1] = {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        adminScope: editForm.adminScope,
        status: editForm.status,
        hospitalId:
          editForm.adminScope === "HOSPITAL" ? editForm.hospitalId || null : null,
      };
      if (editForm.password.trim()) {
        body.password = editForm.password.trim();
      }
      return chainApi.updateAdmin(editingAdmin.id, body);
    },
    onSuccess: () => {
      toast.success("Admin updated");
      setEditOpen(false);
      setEditingAdmin(null);
      setEditForm(emptyForm());
      qc.invalidateQueries({ queryKey: ["chain-admins"] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      toast.error(e.response?.data?.message ?? "Failed to update admin");
    },
  });

  const moveMut = useMutation({
    mutationFn: ({ adminId, hospitalId }: { adminId: string; hospitalId: string }) =>
      chainApi.moveAdminHospital(adminId, hospitalId),
    onSuccess: () => {
      toast.success("Sub-admin moved");
      qc.invalidateQueries({ queryKey: ["chain-admins"] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      toast.error(e.response?.data?.message ?? "Move failed");
    },
  });

  const hospitals = group?.hospitals ?? [];

  const openEdit = (admin: ChainAdminRow) => {
    setEditingAdmin(admin);
    setEditForm({
      name: admin.name,
      email: admin.email,
      password: "",
      adminScope: admin.adminScope,
      hospitalId: admin.hospitalId ?? "",
      status: admin.status === "INACTIVE" ? "INACTIVE" : "ACTIVE",
    });
    setEditOpen(true);
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Admins</h1>
          {group && (
            <p className="text-sm text-muted-foreground mt-1">
              {group.usage.userCount} / {group.maxUsers} admin accounts
            </p>
          )}
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>Add admin</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New admin</DialogTitle>
            </DialogHeader>
            <AdminFormFields
              form={createForm}
              setForm={setCreateForm}
              hospitals={hospitals}
              passwordRequired
            />
            <Button
              className="w-full mt-3"
              disabled={createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              Create
            </Button>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          setEditOpen(open);
          if (!open) {
            setEditingAdmin(null);
            setEditForm(emptyForm());
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit admin</DialogTitle>
          </DialogHeader>
          <AdminFormFields
            form={editForm}
            setForm={setEditForm}
            hospitals={hospitals}
            showStatus
          />
          <Button
            className="w-full mt-3"
            disabled={updateMut.isPending}
            onClick={() => updateMut.mutate()}
          >
            Save changes
          </Button>
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Hospital</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {admins.map((a) => (
              <TableRow key={a.id}>
                <TableCell>{a.name}</TableCell>
                <TableCell>{a.email}</TableCell>
                <TableCell>{a.adminScope === "CHAIN" ? "Chain admin" : "Sub-admin"}</TableCell>
                <TableCell>{a.hospital?.name ?? "—"}</TableCell>
                <TableCell>{a.status === "INACTIVE" ? "Inactive" : "Active"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => openEdit(a)}>
                      Edit
                    </Button>
                    {a.adminScope === "HOSPITAL" && hospitals.length > 1 ? (
                      <Select
                        value={a.hospitalId ?? undefined}
                        onValueChange={(hid) => moveMut.mutate({ adminId: a.id, hospitalId: hid })}
                      >
                        <SelectTrigger className="h-8 w-[140px]">
                          <SelectValue placeholder="Move to…" />
                        </SelectTrigger>
                        <SelectContent>
                          {hospitals
                            .filter((h) => h.id !== a.hospitalId)
                            .map((h) => (
                              <SelectItem key={h.id} value={h.id}>
                                {h.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
